import { useCallback, useMemo } from 'react';

import { Box, Spinner, Text } from '@chakra-ui/react';
import ReactInfiniteScroll, {
  Props as InfiniteScrollProps,
} from 'react-infinite-scroll-component';

export interface InfiniteScrollComponentProps
  extends Omit<InfiniteScrollProps, 'loader' | 'dataLength'> {
  /** Total count of items currently loaded */
  dataLength: number;
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Function to fetch more items */
  next: () => void;
  /** Function to refresh the list (pull-to-refresh) */
  refreshFunction?: () => void;
  /** Additional class names */
  className?: string;
  /** Custom loader element */
  loader?: React.ReactElement;
  /** Whether currently fetching (prevents duplicate calls) */
  isFetching?: boolean;
  /** ID of the scrollable container (required for portals/modals) */
  scrollableTarget?: string;
  /** Custom end message when all items are loaded */
  endMessage?: React.ReactNode;
  /** Show default end message */
  showEndMessage?: boolean;
  /** Scroll threshold (0-1) to trigger next fetch */
  scrollThreshold?: number | string;
  /** Inverse scroll direction (for chat-like UIs) */
  inverse?: boolean;
  /** Custom height for the scroll container */
  height?: number | string;
}

const DefaultLoader = () => (
  <Box py={3} display='flex' justifyContent='center' w='100%'>
    <Spinner size='sm' />
  </Box>
);

const DefaultEndMessage = () => (
  <Text fontSize='xs' color='fonts.secondary' textAlign='center' py={3}>
    No more items to load
  </Text>
);

export const InfiniteScroll: React.FC<InfiniteScrollComponentProps> = ({
  dataLength,
  next,
  loader,
  isFetching,
  endMessage,

  showEndMessage = false,
  scrollThreshold = 0.9,
  inverse = false,

  ...props
}) => {
  const handleNext = useCallback(() => {
    // Prevent duplicate fetches
    if (!isFetching) {
      next();
    }
  }, [isFetching, next]);

  const renderEndMessage = useMemo(() => {
    if (!showEndMessage && !endMessage) return null;
    if (dataLength === 0) return null;
    return endMessage ?? <DefaultEndMessage />;
  }, [showEndMessage, endMessage, dataLength]);

  return (
    <ReactInfiniteScroll
      {...props}
      dataLength={dataLength}
      next={handleNext}
      loader={loader ?? <DefaultLoader />}
      endMessage={renderEndMessage}
      scrollThreshold={scrollThreshold}
      inverse={inverse}
    />
  );
};
